import React from 'react';
import { BriefingQuote } from './components/BriefingQuote';

const Card: React.FC<{ className?: string; height?: number | string }>
  = ({ className = '', height = 180 }) => (
  <div className={`bg-white rounded-lg shadow-sm p-4 ${className}`}>
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
      <div className="h-4 bg-gray-200 rounded w-full animate-pulse" />
      <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse" />
    </div>
    <div style={{ height }} />
  </div>
);

export const TodayDashboardSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#FBF9F6] p-6">
      <div className="mx-auto">
        {/* Header skeleton */}
        <div className="flex items-center justify-between mb-6">
          <div className="space-y-2">
            <div className="h-6 bg-gray-200 rounded w-56 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-80 animate-pulse" />
          </div>
          <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse" />
        </div>

        {/* BriefingQuote renders its own skeleton while loading */}
        <BriefingQuote />

        {/* Main Grid Layout - 4 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Column 1 - Activity Feed skeleton */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-[25px]">
              <div className="h-5 bg-gray-200 rounded w-40 mb-2 animate-pulse" />
              <div className="h-3 bg-gray-200 rounded w-64 mb-6 animate-pulse" />

              <div className="space-y-5">
                {[...Array(3)].map((_, idx) => (
                  <div key={idx} className="">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2 animate-pulse" />
                    <div className="h-3 bg-gray-200 rounded w-full animate-pulse" />
                  </div>
                ))}
              </div>

              <div className="mt-6 h-5 bg-gray-200 rounded w-56 mb-4 animate-pulse" />
              <div className="space-y-3">
                {[...Array(3)].map((_, idx) => (
                  <div key={`attn-${idx}`} className="bg-[#F4F4F4] p-[15px] rounded-lg">
                    <div className="h-4 bg-gray-200 rounded w-2/3 mb-2 animate-pulse" />
                    <div className="h-3 bg-gray-200 rounded w-5/6 mb-3 animate-pulse" />
                    <div className="h-6 bg-gray-200 rounded w-24 animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Column 2 - Metrics + charts skeleton */}
          <div className="lg:col-span-1 space-y-6">
            {/* Metrics card */}
            <div className="bg-white rounded-lg shadow-sm relative h-[230px] p-4">
              <div className="flex justify-between">
                <div className="space-y-2 w-1/2">
                  <div className="h-5 bg-gray-200 rounded w-24 animate-pulse" />
                  <div className="h-5 bg-gray-200 rounded w-28 animate-pulse" />
                </div>
                <div className="h-8 bg-gray-200 rounded w-12 animate-pulse" />
              </div>
              <div className="absolute left-4 right-4 top-1/2 h-px bg-gray-200" />
              <div className="absolute left-4 right-4 bottom-4 flex justify-between items-end">
                <div className="space-y-2 w-1/2">
                  <div className="h-5 bg-gray-200 rounded w-24 animate-pulse" />
                  <div className="h-5 bg-gray-200 rounded w-24 animate-pulse" />
                </div>
                <div className="h-8 bg-gray-200 rounded w-12 animate-pulse" />
              </div>
            </div>

            {/* Time chart skeleton */}
            <Card height={120} />

            {/* Complaint Detection chart skeleton */}
            <Card height={120} />
          </div>

          {/* Column 3 - Donut + metrics skeleton */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="h-5 bg-gray-200 rounded w-40 animate-pulse" />
                <div className="h-5 bg-gray-200 rounded w-24 animate-pulse" />
              </div>
              <div className="flex items-center gap-6">
                <div className="w-28 h-28 bg-gray-200 rounded-full animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-5/6 animate-pulse" />
                  <div className="h-3 bg-gray-200 rounded w-2/3 animate-pulse" />
                  <div className="h-3 bg-gray-200 rounded w-4/5 animate-pulse" />
                </div>
              </div>
            </div>

            <Card height={80} />
            <Card height={80} />
          </div>

          {/* Column 4 - Other analytics skeleton */}
          <div className="lg:col-span-1 space-y-6">
            <Card height={100} />
            <Card height={120} />
            <Card height={100} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodayDashboardSkeleton;

